<template>
  <shared-product-provide :loading="loadingMatching" :product="product" :fromModule="fromModule" :toModule="toModule" :disabledInputList="isMatch ? disabledInputList : []">
    <loader :loading="loadingConditions || loadingProductTypes"/>

    <span v-if="isMatch" slot="header-right" class="badge u-floatRight status success">Este produto é um match</span>

    <form-group :required="true" :label="$t('ageGroup.label')" :columns="3" :error="getError('product.ageGroup', product.productId)">
      <select v-model="product.ageGroup" class="form-select">
        <option :value="null">{{ $t('choose.label') }}</option>
        <option value="adult">{{ $t('ageGroup.adult.label') }}</option>
        <option value="kids">{{ $t('ageGroup.kids.label') }}</option>
        <option value="both">{{ $t('ageGroup.both.label') }}</option>
      </select>
    </form-group>

    <form-group :required="true" :label="$t('condition.label')" :columns="3" :error="getError('condition', product.productId)">
      <select v-model="product.condition" class="form-select">
        <option v-for="condition in conditions" :value="condition.value">{{ condition.label }}</option>
      </select>
    </form-group>

    <form-group :label="$t('productTypes.label')" :columns="3" :error="getError('productType', product.productId)" v-if="productTypes.length" :required="true">
      <multiselect
        v-model="productType"
        :options="productTypes"
        label="name"
        track-by="value"
        selectLabel=""
        selectedLabel=""
        placeholder="Escolha"
      />
    </form-group>

    <div class="l-col-12@md" v-if="attributes.length">
      <div class="divider"><span>Atributos</span></div>
    </div>

    <form-group
      v-for="(attribute, index) in filteredAttributes"
      :key="attribute.id"
      :label="attribute.name"
      :columns="3"
      :error="getError(attribute.name, product.productId)"
      :required="attribute.required"
    >
      <template v-if="attribute.enum && attribute.enum.length">
        <multiselect
          v-model="attribute.value"
          :options="getMultiselectOptions(attribute)"
          label="label"
          track-by="value"
          :placeholder="attribute.name"
        />
      </template>

      <input
        v-else
        class="form-input"
        type="text"
        v-model="attribute.value"
        :placeholder="attribute.example ? attribute.example : attribute.name"
      />
    </form-group>
  </shared-product-provide>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import SharedProductProvide from '@/components/marketplace/shared/provide/Product'
  import FormGroup from '@/components/template/FormGroup'
  import api from '@/api'
  import Multiselect from 'vue-multiselect'
  import Loader from '@/components/template/Loader'
  import notifier from '@/notifier'
  import { cloneDeep, isArray } from 'lodash'

  export default {
    provide: {
      moduleDI: {
        type: 'marketplace',
        identifier: 'amazon'
      },
      maxDescriptionLengthDI: 2000,
      allowDescriptionHtml: false
    },
    data: () => ({
      loadingMatching: false,
      loadingConditions: false,
      loadingProductTypes: false,
      loadingSizesAndColors: false,
      colors: [],
      sizes: [],
      conditions: [],
      productTypes: [],
      productType: null,
      attributes: [],
      isMatch: false,
      disabledInputList: [
        'title',
        'description',
        'brand'
      ],
      partnerId: null
    }),
    props: {
      product: {
        type: Object,
        required: true
      },
      fromModule: {
        type: Object,
        required: true
      },
      toModule: {
        type: Object,
        required: true
      }
    },
    async mounted () {
      this.partnerId = await this.getAmazonPartnerId()

      this.checkMatching(this.partnerId)

      if (this.product.description.length > 2000) {
        this.product.description = this.product.description.substr(0, 2000)
      }

      this.product.condition = null

      this.loadingConditions = true
      await api.amazon.getProductConditions()
        .then(conditions => {
          this.conditions = conditions
        })
        .catch(() => notifier.error('Impossível carregar as condições de produtos da Amazon.', 'Erro'))
        .finally(() => {
          this.loadingConditions = false
        })
      const conditionIndex = this.conditions.findIndex(condition => condition.value === 'New')
      if (conditionIndex !== -1) {
        this.product.condition = this.conditions[conditionIndex].value
      }

      if (!this.isMatch) {
        this.loadingProductTypes = true

        api.amazon.getProductTypes(this.partnerId)
          .then(productTypes => {
            this.productTypes = productTypes
            this.product.productType = ''
          })
          .catch(() => {
            notifier.error('Impossível carregar as "Tipos de Produto" da Amazon.', 'Erro')
          })
          .finally(() => {
            this.loadingProductTypes = false
          })
      }

      this.loadingSizesAndColors = true

      try {
        this.sizes = await api.amazon.getSizes()
        this.colors = await api.amazon.getColors()
      } catch (e) {
        notifier.error('Impossível carregar cores ou tamanhos da Amazon.', 'Erro')
      } finally {
        this.loadingSizesAndColors = false
      }
      this.product.variations.forEach(variation => (this.$set(variation, 'colors', this.getVariationColors(variation))))
      this.product.variations.forEach(variation => (this.$set(variation, 'sizes', cloneDeep(this.sizes))))

      for (let i in this.product.variations) {
        this.product.variations[i].attributes.sizeMap = null
        this.product.variations[i].attributes.colorMap = null

        if (this.product.variations[i].attributes.hasOwnProperty('size') && this.product.variations[i].attributes.size) {
          const sizeMap = this.sizes.find(size => size.label.toLowerCase() === this.product.variations[i].attributes.size.toLowerCase())
          if (sizeMap !== undefined) {
            this.product.variations[i].attributes.sizeMap = sizeMap
          }
        }

        if (this.product.variations[i].attributes.hasOwnProperty('color') && this.product.variations[i].attributes.color) {
          const colorMap = this.colors.find(color => color.label.toLowerCase() === this.product.variations[i].attributes.color.toLowerCase())
          if (colorMap !== undefined) {
            this.product.variations[i].attributes.colorMap = colorMap
          }
        }

        const presetColor = this.$parent.getPresetItemByTypeAndFrom('color', { color: this.product.variations[i].attributes.color })
        if (presetColor !== null && presetColor !== undefined) {
          this.product.variations[i].attributes.colorMap = this.colors.find(color => color.label.toLowerCase() === presetColor.color.toLowerCase())
        }

        const presetSize = this.$parent.getPresetItemByTypeAndFrom('size', { size: this.product.variations[i].attributes.size })
        if (presetSize !== null && presetSize !== undefined) {
          this.product.variations[i].attributes.sizeMap = this.sizes.find(size => size.label.toLowerCase() === presetSize.size.toLowerCase())
        }
      }
      this.$parent.loadingProducts(false)
    },
    methods: {
      ...mapActions('errors', [
        'addError',
        'clearErrors'
      ]),
      formatProduct () {
        this.clearErrors()

        if (!this.isMatch) {
          if (this.productType === null || this.productType === undefined || this.productType === '') {
            this.addError({
              productId: this.product.productId,
              property: `productType`,
              error: {
                message: 'required',
                value: 1
              }
            })
          }

          if (this.product.ageGroup === null || this.product.ageGroup === undefined || this.product.ageGroup === '') {
            this.addError({
              productId: this.product.productId,
              property: `product.ageGroup`,
              error: {
                message: 'required',
                value: 1
              }
            })
          }
        }
        this.product.variations.map(variation => {
          if (!variation.match && variation.selected) {
            if (!variation.attributes.sizeMap) {
              this.addError({
                productId: this.product.productId,
                property: `attributes.sizeMap`,
                sku: variation.sku,
                error: {
                  message: 'required',
                  value: 1
                }
              })
            }

            if (!variation.attributes.colorMap) {
              this.addError({
                productId: this.product.productId,
                property: `attributes.colorMap`,
                sku: variation.sku,
                error: {
                  message: 'required',
                  value: 1
                }
              })
            }

            if (!variation.manufacturerReference || variation.manufacturerReference === '' || variation.manufacturerReference === undefined) {
              this.addError({
                productId: this.product.productId,
                property: `manufacturerReference`,
                sku: variation.sku,
                error: {
                  message: 'required',
                  value: 1
                }
              })
            }
            if (!this.isMatch) {
              if (!variation.gender || variation.gender === '') {
                this.addError({
                  productId: this.product.productId,
                  property: `gender`,
                  sku: variation.sku,
                  error: {
                    message: 'required',
                    value: 1
                  }
                })
              }
            }
          }

          if (variation.amazonAttributes !== undefined) {
            variation.amazonAttributes
              .map(attribute => {
                if ((attribute.value === undefined || attribute.value === null || attribute.value === '') && attribute.required) {
                  this.addError({
                    productId: this.product.productId,
                    property: `attributes.${attribute.id}`,
                    sku: variation.sku,
                    error: {
                      message: 'required',
                      value: 1
                    }
                  })
                }
              })
          }
        })

        this.attributes
          .map(attribute => {
            if (attribute.value === undefined && attribute.required) {
              this.addError({
                productId: this.product.productId,
                property: attribute.id,
                error: {
                  message: 'required',
                  value: 1
                }
              })
            }
          })

        const attributes = this.attributes
          .filter(attribute => {
            if (isArray(attribute.value)) {
              return attribute.value.length
            }
            return (
              attribute.value !== undefined &&
              (typeof attribute.value === 'string' ? attribute.value.trim() !== '' : true)
            )
          })
          .map(attribute => {
            return {
              id: attribute.id,
              label: attribute.attributeId,
              valueId: typeof attribute.value === 'object' ? attribute.value.value : attribute.value,
              valueLabel: attribute.type
            }
          })

        return this.product.variations
          .filter(variation => variation.selected)
          .map(variation => {
            return {
              sku: variation.sku,
              attributes: variation.match ? variation.matchAttributes : attributes,
              title: this.product.title,
              description: this.product.description,
              stock: variation.stock,
              price: variation.price,
              source: this.fromModule,
              ean: variation.ean,
              images: variation.images.length ? variation.images : null,
              width: variation.width,
              height: variation.height,
              weight: variation.weight,
              length: variation.length,
              category: variation.match ? variation.productType : this.productType.value,
              useVariation: this.productType ? this.productType.useVariation : false,
              productType: variation.match ? variation.productType : this.productType.value,
              brand: variation.match ? variation.brand : this.product.brand,
              manufacturerReference: variation.manufacturerReference,
              productId: this.product.productId,
              match: variation.match,
              condition: this.product.condition,
              color: variation.attributes.color !== undefined && variation.attributes.color !== '' && variation.attributes.color !== null ? variation.attributes.color : 'Única',
              colorMap: variation.attributes.colorMap !== undefined && variation.attributes.colorMap !== '' && variation.attributes.colorMap !== null ? variation.attributes.colorMap.value : '',
              size: variation.attributes.size !== undefined && variation.attributes.size !== '' && variation.attributes.size !== null ? variation.attributes.size : 'Único',
              sizeMap: variation.attributes.sizeMap !== undefined && variation.attributes.sizeMap !== '' && variation.attributes.sizeMap !== null ? variation.attributes.sizeMap.value : '',
              gender: variation.gender,
              ageGroup: this.product.ageGroup,
              crossDocking: {
                quantity: variation.crossDocking
              },
              original: {
                product: this.product,
                variation: variation
              }
            }
          }
        )
      },
      async updateAttributes () {
        this.loadingProductTypes = true

        try {
          const sizeAttr = this.attributes.find(attr => this.isSizeAttribute(attr))
          const sizeClassAttr = this.attributes.find(attr => this.isSizeClassAttribute(attr))

          let fallbackSizeValue = null

          if (sizeAttr && sizeClassAttr && sizeClassAttr.value) {
            const filteredSizes = this.filterAndUpdateSizes(
              sizeClassAttr.value,
              sizeAttr,
              true
            )

            if (filteredSizes.length > 0) {
              fallbackSizeValue = filteredSizes[0].value
            }
          }

          const attributesToSend = this.attributes.map(attr => {
            if (attr === sizeAttr && fallbackSizeValue !== null) {
              const newAttr = Object.assign({}, attr)
              newAttr.value = fallbackSizeValue
              return newAttr
            }

            return attr
          })

          const res = await api.amazon.getProductTypeAttributes(
            this.productType.value,
            this.partnerId,
            { attributes: attributesToSend }
          )

          if (Array.isArray(res)) {
            this.mergeAttributes(this.attributes, res)
          }
        } catch (e) {
          console.error('Erro ao atualizar atributos', e)
        } finally {
          setTimeout(() => {
            this.loadingProductTypes = false
          }, 1000)
        }
      },
      mergeAttributes (current, incoming) {
        var map = {}

        for (var i = 0; i < current.length; i++) {
          var attr = current[i]
          map[attr.attributeId] = attr
        }

        for (var j = 0; j < incoming.length; j++) {
          var newAttr = incoming[j]
          if (!map[newAttr.attributeId]) {
            map[newAttr.attributeId] = newAttr
          } else {
            var existing = map[newAttr.attributeId]
            if (!existing.value && newAttr.value) {
              existing.value = newAttr.value
            }

            for (var key in newAttr) {
              if (newAttr.hasOwnProperty(key) && typeof existing[key] === 'undefined') {
                existing[key] = newAttr[key]
              }
            }

            map[newAttr.attributeId] = existing
          }
        }

        this.attributes = Object.keys(map).map(function (key) { return map[key] })
      },
      filterAndUpdateSizes (sizeClass, sizeAttr, getList) {
        let filteredSizes = []

        if (sizeClass && sizeClass.value && sizeAttr && sizeAttr.sizeClassOptions) {
          filteredSizes = sizeAttr.sizeClassOptions[sizeClass.value] || sizeAttr.sizeOptions || []
        }

        if (getList) {
          return filteredSizes
        }

        this.applySizes(filteredSizes)

        return filteredSizes
      },
      isSizeAttribute (attribute) {
        const attributeName = String(attribute.attributeId || '').split('/').pop().split('.')[0]
        return attribute.id === 'size' || attributeName === 'size'
      },
      isSizeClassAttribute (attribute) {
        const attributeName = String(attribute.attributeId || '').split('/').pop().split('.')[0]
        return attribute.id === 'size_class' || attributeName === 'size_class'
      },
      applySizes (sizes) {
        this.product.variations.forEach(variation => {
          variation.sizes = cloneDeep(sizes)

          if (variation.attributes.sizeMap) {
            const isValid = sizes.some(
              size => size.value === variation.attributes.sizeMap.value
            )
            if (!isValid) {
              variation.attributes.sizeMap = null
            }
          }
        })
      },
      applyVariationOwnSizes () {
        this.product.variations.forEach(variation => {
          const size = this.getVariationAttributeText(variation, 'size', 'Único')

          if (variation.sizes && variation.sizes.length === 1 && variation.sizes[0].value === size) {
            return
          }

          this.$set(variation, 'sizes', [{ value: size, label: size }])
          variation.attributes.sizeMap = { value: size, label: size }
        })
      },
      getVariationColors (variation) {
        const color = this.getVariationAttributeText(variation, 'color', 'Única')
        const colors = cloneDeep(this.colors)
        const hasColor = colors.some(option => String(option.label).toLowerCase() === color.toLowerCase())

        return hasColor ? colors : [{ value: color, label: color }, ...colors]
      },
      getVariationAttributeText (variation, attribute, fallback) {
        const value = variation.attributes ? variation.attributes[attribute] : null

        return String(value === undefined || value === null ? '' : value).trim() || fallback
      },
      isVariationSizePlaceholder (attribute) {
        return attribute.value === 'sizeMap' && !(attribute.enum && attribute.enum.length)
      },
      async getAmazonPartnerId () {
        try {
          const accounts = (await api.amazon.getAccounts({})).items
          return accounts.length > 0 ? accounts[0].partnerId : null
        } catch (e) {
          return null
        }
      },
      async fillAttributes (productType) {
        this.loadingProductTypes = true

        api.amazon.getProductTypeAttributes(productType.value, this.partnerId)
          .then(attributes => {
            this.attributes = attributes
            this.productType.attributes = ''
          })
          .catch(() => {
            notifier.error(`Impossível carregar atributos de "${productType.name}".`, 'Erro')
          })
          .finally(() => {
            this.loadingProductTypes = false
          })
      },
      getMultiselectOptions (attribute) {
        if (attribute.enum && attribute.enum.length && typeof attribute.enum[0] === 'object') {
          return attribute.enum.map(e => ({
            value: e.value,
            label: e.label || e.value
          }))
        }

        return (attribute.enum || []).map((enumVal, idx) => ({
          value: enumVal,
          label: attribute.enumNames && attribute.enumNames[idx] ? attribute.enumNames[idx] : enumVal
        }))
      },
      async checkMatching (partnerId) {
        this.loadingMatching = true

        try {
          const eanMatchingList = await api.amazon.getMatching(this.product.variations.map(variation => variation.ean), partnerId)

          for (const variation of this.product.variations) {
            variation.match = eanMatchingList.eans.indexOf(variation.ean) !== -1
            const matchContent = eanMatchingList.eansContents[variation.ean]

            if (matchContent) {
              variation.productType = matchContent.productType
              variation.brand = matchContent.brand
              variation.matchAttributes = matchContent.attributes
            }
          }

          this.isMatch = this.product.variations.every(variation => variation.match)
        } catch (e) {
          for (const variation of this.product.variations) {
            variation.match = false
          }
        }

        this.loadingMatching = false
      }
    },
    computed: {
      ...mapGetters('errors', [
        'getError',
        'getErrors'
      ]),
      sizeAttribute: function () {
        if (!this.product.variations || this.product.variations.length === 0) {
          return false
        }
        const sizes = this.product.variations
          .map(v => v.attributes && v.attributes.size)
          .filter(s => s !== undefined && s !== null && s !== '')
        if (sizes.length === 0) {
          return true
        }
        return new Set(sizes).size === 1
      },
      filteredAttributes: function () {
        const showSize = this.sizeAttribute
        return this.attributes.filter(attr => {
          if (['language_tag', 'marketplace_id'].includes(attr.id)) return false
          if (this.isSizeAttribute(attr)) return showSize && !this.isVariationSizePlaceholder(attr)
          if (attr.hide === true) return false
          return true
        })
      },
      allAttributesFilled: function () {
        var attrs = this.attributes
        if (!attrs || attrs.length === 0) return false

        for (let i = 0; i < attrs.length; i++) {
          const attr = attrs[i]

          if (attr.hide || !attr.required) {
            continue
          }

          let val = attr.value

          if (typeof val === 'object' && val !== null) {
            if ('value' in val) {
              val = val.value
            } else if (Object.keys(val).length === 0) {
              return false
            }
          }

          if (val === null || val === undefined) {
            return false
          }

          if (typeof val === 'string' && val === '') {
            return false
          }
        }
        return true
      }
    },
    watch: {
      productType (value) {
        if (!this.isMatch) {
          if (value) {
            this.fillAttributes(value)
          }
        }
      },
      attributes: {
        deep: true,
        handler (newAttributes) {
          const sizeAttrs = newAttributes.filter(attr => this.isSizeAttribute(attr))
          const sizeAttr = sizeAttrs.find(attr => attr.sizeOptions) || sizeAttrs.find(attr => attr.hide !== true) || sizeAttrs[0]
          const sizeClassAttr = newAttributes.find(attr => this.isSizeClassAttribute(attr))

          if (sizeAttr && sizeAttr.sizeOptions) {
            if (sizeClassAttr && sizeClassAttr.value) {
              this.filterAndUpdateSizes(sizeClassAttr.value, sizeAttr, false)
            } else {
              this.applySizes(sizeAttr.sizeOptions)
            }
          } else if (newAttributes.length) {
            this.applyVariationOwnSizes()
          }
        },
        immediate: true
      },
      allAttributesFilled: function (filled) {
        if (filled && !this.loadingProductTypes) {
          this.updateAttributes()
        }
      }
    },
    components: {
      SharedProductProvide,
      FormGroup,
      Multiselect,
      Loader
    }
  }
</script>



// WEBPACK FOOTER //
// Product.vue?73a8126c